import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import axios from "axios";
import Logo from "@/components/Logo";
import { ArrowUpRight, ArrowDownRight, TrendingUp, TrendingDown, Download } from "lucide-react";

export default function SharedView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [pwErr, setPwErr] = useState(null);
  const [meta, setMeta] = useState(null);

  const fetchWith = (pw) => {
    const url = pw ? `${API}/share/public/${token}?password=${encodeURIComponent(pw)}` : `${API}/share/public/${token}`;
    return axios.get(url)
      .then((r) => { setData(r.data); setNeedsPassword(false); setPwErr(null); })
      .catch((e) => {
        if (e?.response?.status === 401) {
          setNeedsPassword(true);
          if (pw) setPwErr("Wrong password. Try again.");
        } else {
          setError(e?.response?.data?.detail || "Link is invalid or has expired.");
        }
      });
  };

  useEffect(() => {
    // Ask meta first so we know if a password gate is required before hitting the data endpoint
    axios.get(`${API}/share/public/${token}/meta`)
      .then((r) => { setMeta(r.data); if (r.data.has_password) setNeedsPassword(true); else fetchWith(""); })
      .catch((e) => setError(e?.response?.data?.detail || "Link is invalid or has expired."));
  }, [token]); // eslint-disable-line

  const downloadPdf = async () => {
    try {
      setPdfBusy(true);
      const pw = password ? `?password=${encodeURIComponent(password)}` : "";
      const resp = await fetch(`${API}/share/public/${token}/pdf${pw}`);
      if (!resp.ok) throw new Error("failed");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : "report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    } finally {
      setPdfBusy(false);
    }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F8F9FA]">
      <div className="card-crisp p-10 max-w-md text-center">
        <div className="text-lg font-display font-semibold text-[#0A0D14]">Link unavailable</div>
        <div className="mt-2 text-sm text-[#525B6A]">{error}</div>
      </div>
    </div>
  );
  if (needsPassword) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F8F9FA]">
      <div className="card-crisp p-10 max-w-md w-full text-center" data-testid="share-password-gate">
        <Logo />
        <div className="mt-6 text-lg font-display font-semibold text-[#0A0D14]">This report is protected</div>
        <div className="mt-2 text-sm text-[#525B6A]">Enter the password shared by the agency to view this report.</div>
        <input
          type="password"
          data-testid="share-password-input"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && fetchWith(password)}
          className="mt-6 w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm text-center tabular-nums"
          placeholder="Password"
        />
        {pwErr && <div className="mt-2 text-xs text-red-600">{pwErr}</div>}
        <button onClick={() => fetchWith(password)} data-testid="share-password-submit" className="mt-4 btn-primary w-full px-4 py-2.5 rounded-md text-sm">Unlock</button>
      </div>
    </div>
  );
  if (!data) return <div className="p-10 text-[#8A94A6]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {data.kind === "report" ? <BrandedReport data={data} onDownloadPdf={downloadPdf} pdfBusy={pdfBusy} /> : <StandardShare data={data} />}
    </div>
  );
}

function StandardShare({ data }) {
  return (
    <>
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="text-xs text-[#8A94A6]">Public share · read-only</div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="text-xs text-[#8A94A6] uppercase tracking-wider">Project</div>
        <h1 className="mt-1 text-3xl font-display font-bold text-[#0A0D14]">{data.project.name}</h1>
        <div className="mt-1 text-sm text-[#525B6A]">{data.project.domain}{data.project.last_updated_at ? ` · updated ${new Date(data.project.last_updated_at).toLocaleString()}` : ''}</div>

        {data.kind === "project" && (
          <div className="mt-8 card-crisp overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F8F9FA] border-b border-[#E2E8F0] text-left text-[10px] uppercase tracking-wider text-[#8A94A6]">
                  <tr>
                    <th className="px-4 py-3">Keyword</th>
                    <th className="px-3 py-3">Country</th>
                    <th className="px-3 py-3">Device</th>
                    <th className="px-3 py-3">Rank</th>
                    <th className="px-3 py-3">Change</th>
                    <th className="px-3 py-3">Volume</th>
                  </tr>
                </thead>
                <tbody>
                  {data.keywords.map((k) => {
                    const c = k.current_rank, p = k.previous_rank;
                    let delta = null, dir = null;
                    if (c !== null && p !== null) { delta = Math.abs(p - c); dir = c < p ? 'up' : c > p ? 'down' : 'flat'; }
                    return (
                      <tr key={k.id} className="border-b border-[#E2E8F0]">
                        <td className="px-4 py-3 text-[#0A0D14] font-medium">{k.keyword}</td>
                        <td className="px-3 py-3 text-[#525B6A]">{k.country}</td>
                        <td className="px-3 py-3 text-[#525B6A] capitalize">{k.device}</td>
                        <td className="px-3 py-3 tabular-nums font-medium">{c ? `#${c}` : '—'}</td>
                        <td className="px-3 py-3">
                          {dir === 'up' && <span className="inline-flex items-center gap-0.5 text-emerald-600 tabular-nums"><ArrowUpRight className="h-3.5 w-3.5" />{delta}</span>}
                          {dir === 'down' && <span className="inline-flex items-center gap-0.5 text-red-600 tabular-nums"><ArrowDownRight className="h-3.5 w-3.5" />{delta}</span>}
                          {dir === 'flat' && <span className="text-[#8A94A6] tabular-nums">0</span>}
                        </td>
                        <td className="px-3 py-3 tabular-nums text-[#525B6A]">{k.search_volume ? k.search_volume.toLocaleString() : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data.kind === "keyword" && (
          <div className="mt-8 card-crisp p-6">
            <div className="text-xs text-[#8A94A6] uppercase tracking-wider">Keyword</div>
            <div className="mt-1 text-2xl font-display font-semibold text-[#0A0D14]">{data.keyword.keyword}</div>
            <div className="mt-1 text-sm text-[#525B6A]">{data.keyword.country} · {data.keyword.device} · {data.keyword.engine}</div>
            <div className="mt-6 grid grid-cols-3 gap-4">
              <div className="border border-[#E2E8F0] rounded-md p-4"><div className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Current</div><div className="stat-value text-3xl font-bold text-[#0047FF] mt-1">{data.keyword.current_rank ? `#${data.keyword.current_rank}` : '—'}</div></div>
              <div className="border border-[#E2E8F0] rounded-md p-4"><div className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Previous</div><div className="stat-value text-3xl font-bold text-[#525B6A] mt-1">{data.keyword.previous_rank ? `#${data.keyword.previous_rank}` : '—'}</div></div>
              <div className="border border-[#E2E8F0] rounded-md p-4"><div className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Best</div><div className="stat-value text-3xl font-bold text-emerald-600 mt-1">{data.keyword.best_rank ? `#${data.keyword.best_rank}` : '—'}</div></div>
            </div>
          </div>
        )}

        <footer className="mt-14 text-center text-xs text-[#8A94A6]">
          Powered by <a href="/" className="text-[#0047FF]">SerpNex</a> · Daily rank tracking · Transparency-first
        </footer>
      </main>
    </>
  );
}

function BrandedReport({ data, onDownloadPdf, pdfBusy }) {
  const brand = data.brand || {};
  const s = data.summary || {};
  const accent = brand.primary_color || "#0047FF";
  const isDefault = brand.is_default;
  return (
    <div data-testid="branded-report">
      <div style={{ background: accent, height: 6 }} />
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt={brand.company_name || "logo"} className="h-9 max-w-[200px] object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
              <div className="text-xl font-display font-bold text-[#0A0D14]">{brand.company_name || "SerpNex"}</div>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={onDownloadPdf}
              disabled={pdfBusy}
              data-testid="download-pdf-btn"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md border border-[#E2E8F0] bg-white hover:bg-[#F8F9FA] text-[#0A0D14] disabled:opacity-50 transition"
            >
              <Download className="h-3.5 w-3.5" />
              {pdfBusy ? "Preparing…" : "Download PDF"}
            </button>
            <div className="text-xs uppercase tracking-wider font-semibold hidden sm:block" style={{ color: accent }}>Client report</div>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: accent }}>{brand.company_name || "SerpNex"} · Performance report</div>
        <h1 className="mt-2 text-4xl font-display font-bold text-[#0A0D14]">{s.project?.name}</h1>
        <div className="mt-1 text-[#525B6A]">{s.project?.domain}</div>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat label="Tracked" value={s.tracked ?? 0} />
          <Stat label="Top 10" value={s.top10 ?? 0} accent={accent} />
          <Stat label="Improved" value={s.improved ?? 0} color="#059669" />
          <Stat label="Dropped" value={s.dropped ?? 0} color="#DC2626" />
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-8">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-semibold mb-3">Biggest wins</div>
            <div className="card-crisp overflow-hidden">
              {(s.biggest_wins || []).map((w, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0] last:border-b-0">
                  <div className="text-sm text-[#0A0D14]">{w.keyword}</div>
                  <div className="tabular-nums font-medium text-emerald-600 inline-flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" />{w.delta} · #{w.current}</div>
                </div>
              ))}
              {(s.biggest_wins || []).length === 0 && <div className="text-sm text-[#8A94A6] px-4 py-6">No significant gains this period.</div>}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-semibold mb-3">Biggest drops</div>
            <div className="card-crisp overflow-hidden">
              {(s.biggest_losses || []).map((w, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0] last:border-b-0">
                  <div className="text-sm text-[#0A0D14]">{w.keyword}</div>
                  <div className="tabular-nums font-medium text-red-600 inline-flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5" />{w.delta} · #{w.current}</div>
                </div>
              ))}
              {(s.biggest_losses || []).length === 0 && <div className="text-sm text-[#8A94A6] px-4 py-6">No significant drops this period.</div>}
            </div>
          </div>
        </div>

        <footer className="mt-16 border-t border-[#E2E8F0] pt-6 flex items-center justify-between text-xs text-[#8A94A6]">
          <div>{brand.tagline || "Daily rank tracking · transparency-first"}</div>
          {isDefault && <div>Powered by <a href="/" className="text-[#0047FF]">SerpNex</a></div>}
        </footer>
      </main>
    </div>
  );
}

function Stat({ label, value, accent, color }) {
  return (
    <div className="card-crisp p-4">
      <div className="text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium">{label}</div>
      <div className="stat-value text-3xl font-bold mt-1" style={{ color: color || accent || "#0A0D14" }}>{value}</div>
    </div>
  );
}
